import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { useStore } from '../store/useStore'
import type { Todo } from '../lib/supabase'
import { getColorWithOpacity } from '../utils/colorUtils'
import { getTodoImages, getThumbnailUrl } from '../lib/imageStorage'
import { getTodoFiles } from '../lib/fileStorage'
import TodoImageModal from './TodoImageModal'
import { Tooltip } from './Tooltip'
import TodoFileModal from './TodoFileModal'
import { useFeedbackSettings } from '../hooks/useFeedbackSettings'
import { triggerFeedback, initializeFeedback } from '../lib/feedback'
import DOMPurify from 'dompurify'
import RichTextEditor from './RichTextEditor'
import ReactMarkdown from 'react-markdown'
import { useSwipeable } from 'react-swipeable'
import {
  Clock,
  Check,
  X,
  Share2,
  Bell,
  Calendar
} from 'lucide-react'

// Button SVG images
const ButtonE = '/img/buttons/ButtonE.svg' // タスクを編集
const ButtonF = '/img/buttons/ButtonF.svg' // ゴミ箱に移動
const ButtonH = '/img/buttons/ButtonH.svg' // 画像を管理
const ButtonI = '/img/buttons/ButtonI.svg' // ファイルを管理
const ButtonJ = '/img/buttons/ButtonJ.svg' // 担当者を設定
const ButtonK = '/img/buttons/ButtonK.svg' // 進捗メッセージ
const ButtonL = '/img/buttons/ButtonL.svg' // タスクを複製
import { AssignedUserBadge } from './team/AssignedUserBadge'
import { ProgressBar } from './team/ProgressBar'
import { AssignTaskModal } from './team/AssignTaskModal'
import { TaskProgressPanel } from './team/TaskProgressPanel'
import { ShareModal } from './social/ShareModal'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { LocalNotifications } from '@capacitor/local-notifications'
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem'
import { taskToICalendar } from '../utils/icalendar'
import { toDateTimeLocal, toDateOnly, toTimeOnly, combineDateTime, parseLocalDate, parseLocalDateTime, dateToISOString } from '../utils/dateUtils'
import { useAuth } from '../contexts/AuthContext'
import { getUserTeamRole } from '../services/teamService'
import ConfirmDialog from './ConfirmDialog'

const MAX_TITLE_LENGTH = 500

interface TodoItemProps {
  todo: Todo
  onTriggerCelebration?: (x: number, y: number) => void
}

export default function TodoItem({ todo, onTriggerCelebration }: TodoItemProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { updateTodo, addTodo, projects, swipedTodoId, setSwipedTodoId, todoFilesChangedAt } = useStore()
  const { soundEnabled } = useFeedbackSettings()
  const [isEditing, setIsEditing] = useState(false)
  const [userRole, setUserRole] = useState<string | null>(null)

  // Consolidated edit form state
  const [editForm, setEditForm] = useState({
    title: todo.title,
    description: todo.description || '',
    startDate: toDateOnly(todo.start_date),
    dueDate: toDateTimeLocal(todo.due_date),
    date: toDateOnly(todo.due_date),
    time: toTimeOnly(todo.due_date),
    projectId: todo.project_id
  })
  const [showImageModal, setShowImageModal] = useState(false)
  const [viewerImageId, setViewerImageId] = useState<string | null>(null) // サムネイルクリック時に全画面表示する画像ID
  const [showFileModal, setShowFileModal] = useState(false)
  const [imageCount, setImageCount] = useState(0)
  const [fileCount, setFileCount] = useState(0)
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)
  const [titleError, setTitleError] = useState('')
  const [hasNotification, setHasNotification] = useState(false)
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [isSwipeConfirmed, setIsSwipeConfirmed] = useState(false) // スワイプが確定したかどうか
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showProgressPanel, setShowProgressPanel] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showProjectChangeConfirm, setShowProjectChangeConfirm] = useState(false)
  const [pendingProjectId, setPendingProjectId] = useState<string | null>(null)
  const statusSelectRef = useRef<HTMLSelectElement>(null)
  const startDateInputRef = useRef<HTMLInputElement>(null)
  const dateInputRef = useRef<HTMLInputElement>(null)
  const timeInputRef = useRef<HTMLInputElement>(null)

  // 他のタスクがスワイプされたときに、自身のスワイプをリセット
  useEffect(() => {
    if (swipedTodoId !== null && swipedTodoId !== todo.id && (swipeOffset !== 0 || isSwipeConfirmed)) {
      setSwipeOffset(0)
      setIsSwipeConfirmed(false)
    }
  }, [swipedTodoId, todo.id, swipeOffset, isSwipeConfirmed])

  // コンポーネントがマウントされた時、swipeOffsetを確実に0にリセット
  useEffect(() => {
    setSwipeOffset(0)
    setIsSwipeConfirmed(false)
  }, [todo.id])

  // Load image and file counts on mount and when todo changes
  useEffect(() => {
    const loadCounts = async () => {
      const [imageResult, fileResult] = await Promise.all([
        getTodoImages(todo.id),
        getTodoFiles(todo.id)
      ])
      if (imageResult.success && imageResult.images) {
        setImageCount(imageResult.images.length)
      }
      if (fileResult.success && fileResult.files) {
        setFileCount(fileResult.files.length)
      }
    }
    loadCounts()
  }, [todo.id, todo.updated_at, todoFilesChangedAt]) // todoFilesChangedAtを監視してファイルのリアルタイム同期に対応

  // Load thumbnail URL when thumbnail_image_id changes
  useEffect(() => {
    const loadThumbnail = async () => {
      // Clear thumbnail if no thumbnail_image_id is set
      if (!todo.thumbnail_image_id) {
        setThumbnailUrl(null)
        return
      }

      // Get images and find the thumbnail
      const result = await getTodoImages(todo.id)
      if (result.success && result.images) {
        const thumbnailImage = result.images.find(img => img.id === todo.thumbnail_image_id)
        if (thumbnailImage) {
          const signedUrl = await getThumbnailUrl(thumbnailImage.image_url)
          setThumbnailUrl(signedUrl)
        } else {
          // Thumbnail image not found (may have been deleted)
          setThumbnailUrl(null)
        }
      } else {
        setThumbnailUrl(null)
      }
    }
    loadThumbnail()
  }, [todo.id, todo.thumbnail_image_id])

  // Load user role for team projects
  useEffect(() => {
    const loadUserRole = async () => {
      if (!user) {
        setUserRole(null)
        return
      }

      // Find the project for this todo
      const project = projects.find(p => p.id === todo.project_id)
      if (!project || !project.team_id) {
        // Not a team project, user has full access
        setUserRole('owner')
        return
      }

      // Get user's role in the team
      const role = await getUserTeamRole(project.team_id, user.id)
      setUserRole(role)
    }

    loadUserRole()
  }, [user, todo.project_id, projects])

  // Check if user is viewer (read-only access)
  const isViewer = userRole === 'viewer'

  const handleStatusChange = async (newStatus: Todo['status']) => {
    // ユーザー操作内でAudioContextを初期化
    initializeFeedback()

    // 完了時の効果音フィードバックと祝福アニメーション
    if (newStatus === 'completed') {
      // ステータスドロップダウンの位置を取得してcelebrationをトリガー
      if (statusSelectRef.current && onTriggerCelebration) {
        const rect = statusSelectRef.current.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2
        onTriggerCelebration(centerX, centerY)
      }

      triggerFeedback('complete', { soundEnabled })

      // すぐにステータスを更新（タスクは先に消える）
      await updateTodo(todo.id, { status: newStatus })
    } else {
      await updateTodo(todo.id, { status: newStatus })
    }
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value
    setEditForm(prev => ({ ...prev, title: newTitle }))

    if (newTitle.length > MAX_TITLE_LENGTH) {
      setTitleError(`タスク名は${MAX_TITLE_LENGTH}文字以内で入力してください（現在: ${newTitle.length}文字）`)
    } else {
      setTitleError('')
    }
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value
    const dueDate = combineDateTime(newDate, editForm.time)
    setEditForm(prev => ({ ...prev, date: newDate, dueDate }))
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value
    const dueDate = combineDateTime(editForm.date, newTime)
    setEditForm(prev => ({ ...prev, time: newTime, dueDate }))
  }

  const clearStartDate = () => {
    setEditForm(prev => ({ ...prev, startDate: '' }))
  }

  const clearDate = () => {
    setEditForm(prev => ({ ...prev, date: '', time: '', dueDate: '' }))
  }

  const handleUpdate = async () => {
    // 文字数チェック
    if (editForm.title.length > MAX_TITLE_LENGTH) {
      setTitleError(`タスク名は${MAX_TITLE_LENGTH}文字以内で入力してください（現在: ${editForm.title.length}文字）`)
      return
    }

    await updateTodo(todo.id, {
      title: editForm.title,
      description: editForm.description || null,
      start_date: editForm.startDate ? dateToISOString(editForm.startDate) : null,
      due_date: editForm.dueDate || null,
      project_id: editForm.projectId
    })
    setIsEditing(false)
    setTitleError('')
  }

  const handleProjectChange = (newProjectId: string) => {
    setPendingProjectId(newProjectId)
    setShowProjectChangeConfirm(true)
  }

  const handleProjectChangeConfirm = async () => {
    if (pendingProjectId) {
      await updateTodo(todo.id, { project_id: pendingProjectId })
    }
    setShowProjectChangeConfirm(false)
    setPendingProjectId(null)
  }

  // タスクの複製
  const handleDuplicateTask = async () => {
    try {
      // 複製するタスクのデータを準備
      const duplicatedTodo = {
        title: `${todo.title} (コピー)`,
        description: todo.description,
        priority: todo.priority,
        status: 'pending' as const, // 常に未着手で作成
        start_date: todo.start_date,
        due_date: todo.due_date,
        project_id: todo.project_id,
        // チームタスクの場合は割り当てをクリア（自分のタスクとして作成）
        assigned_to_user_id: null,
        assignment_status: 'unassigned' as const,
        progress_percentage: 0,
        deleted_at: null,
        thumbnail_image_id: null,
        thumbnail_size: 'small' as const
      }

      await addTodo(duplicatedTodo)

      // 成功フィードバック
      triggerFeedback('create', { soundEnabled })

    } catch (error) {
      console.error('Error duplicating task:', error)
      alert('タスクの複製に失敗しました')
    }
  }

  // 通知の権限チェックと状態確認
  useEffect(() => {
    const checkNotificationStatus = async () => {
      if (!Capacitor.isNativePlatform() || !todo.due_date) {
        return
      }

      try {
        // 通知IDを生成（タスクIDベース）
        const notificationId = parseInt(todo.id.replace(/-/g, '').substring(0, 8), 16)

        // スケジュール済み通知をチェック
        const pending = await LocalNotifications.getPending()
        const hasScheduled = pending.notifications.some(n => n.id === notificationId)
        setHasNotification(hasScheduled)
      } catch (error) {
        console.error('Error checking notification status:', error)
      }
    }

    checkNotificationStatus()
  }, [todo.id, todo.due_date])

  // 通知をスケジュール
  const scheduleNotification = async () => {
    if (!Capacitor.isNativePlatform() || !todo.due_date) {
      alert('通知を設定するには期限を設定してください')
      return
    }

    try {
      // 権限チェック
      const permResult = await LocalNotifications.checkPermissions()
      if (permResult.display !== 'granted') {
        const requestResult = await LocalNotifications.requestPermissions()
        if (requestResult.display !== 'granted') {
          alert('通知の権限が必要です')
          return
        }
      }

      const dueDate = new Date(todo.due_date)
      const now = new Date()

      if (dueDate <= now) {
        alert('期限が過去の日時です')
        return
      }

      // 通知IDを生成（タスクIDベース）
      const notificationId = parseInt(todo.id.replace(/-/g, '').substring(0, 8), 16)

      // 期限の30分前に通知
      const notificationTime = new Date(dueDate.getTime() - 30 * 60 * 1000)

      if (notificationTime <= now) {
        alert('通知時刻が過去になるため、期限時刻に通知します')
        await LocalNotifications.schedule({
          notifications: [
            {
              id: notificationId,
              title: '📋 タスクの期限です',
              body: todo.title,
              schedule: { at: dueDate },
              sound: undefined,
              attachments: undefined,
              actionTypeId: '',
              extra: { todoId: todo.id }
            }
          ]
        })
      } else {
        await LocalNotifications.schedule({
          notifications: [
            {
              id: notificationId,
              title: '📋 タスクの期限が近づいています',
              body: `${todo.title} (30分前)`,
              schedule: { at: notificationTime },
              sound: undefined,
              attachments: undefined,
              actionTypeId: '',
              extra: { todoId: todo.id }
            }
          ]
        })
      }

      setHasNotification(true)
      alert('通知を設定しました')
    } catch (error: any) {
      console.error('Error scheduling notification:', error)
      alert('通知の設定に失敗しました')
    }
  }

  // 通知をキャンセル
  const cancelNotification = async () => {
    if (!Capacitor.isNativePlatform()) {
      return
    }

    try {
      const notificationId = parseInt(todo.id.replace(/-/g, '').substring(0, 8), 16)
      await LocalNotifications.cancel({
        notifications: [{ id: notificationId }]
      })

      setHasNotification(false)
      alert('通知をキャンセルしました')
    } catch (error) {
      console.error('Error canceling notification:', error)
      alert('通知のキャンセルに失敗しました')
    }
  }

  // タスクをカレンダーにエクスポート
  const handleExportToCalendar = async () => {
    if (!todo.due_date) {
      alert('カレンダーにエクスポートするには期限を設定してください')
      return
    }

    try {
      // iCalendar形式に変換
      const icsContent = taskToICalendar(todo)

      if (Capacitor.isNativePlatform()) {
        // ネイティブアプリの場合：ファイルとして保存して共有
        const fileName = `task-${todo.id}.ics`

        const result = await Filesystem.writeFile({
          path: fileName,
          data: icsContent,
          directory: Directory.Cache,
          encoding: Encoding.UTF8
        })

        // カレンダーアプリで開く
        await Share.share({
          title: 'タスクをカレンダーに追加',
          text: `${todo.title}をカレンダーに追加`,
          url: result.uri,
          dialogTitle: 'カレンダーアプリで開く'
        })
      } else {
        // Webの場合：ダウンロード
        const blob = new Blob([icsContent], { type: 'text/calendar' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${todo.title}.ics`
        a.click()
        URL.revokeObjectURL(url)

        alert('iCalendarファイルをダウンロードしました。カレンダーアプリにインポートしてください。')
      }
    } catch (error: any) {
      console.error('Error exporting to calendar:', error)
      alert('カレンダーへのエクスポートに失敗しました')
    }
  }

  // スワイプハンドラー
  const swipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      // スワイプ中のオフセットを追跡（確定後は動かない）
      if (!isEditing && !isSwipeConfirmed) {
        const offset = eventData.deltaX
        // 他のタスクがスワイプ中なら、そのタスクをリセット
        if (swipedTodoId && swipedTodoId !== todo.id) {
          setSwipedTodoId(null)
        }
        // -250から250の範囲に制限、スワイプ中はボタンを表示しない
        setSwipeOffset(Math.max(-250, Math.min(250, offset)))
      }
    },
    onSwiped: () => {
      // スワイプ終了時：120px以上なら確定してボタン表示、それ以下なら中心に戻る
      if (!isDeleting && !isEditing && !isSwipeConfirmed) {
        const confirmThreshold = 120 // 確定する閾値
        if (Math.abs(swipeOffset) >= confirmThreshold) {
          // 120px以上で離したら確定（80pxでボタン表示）
          if (swipeOffset < 0) {
            setSwipeOffset(-80)
          } else {
            setSwipeOffset(80)
          }
          setIsSwipeConfirmed(true)
          setSwipedTodoId(todo.id)
        } else {
          // 120px未満で離したら中心に戻る
          setSwipeOffset(0)
          setSwipedTodoId(null)
          setIsSwipeConfirmed(false)
        }
      }
    },
    trackMouse: false,
    trackTouch: true,
    delta: 10 // スワイプと認識する最小移動距離
  })

  // 古いテキストベースのシェア機能（現在は未使用）
  // const handleShare = async () => {
  //   try {
  //     const project = projects.find(p => p.id === todo.project_id)
  //     const projectName = project?.name || 'プロジェクトなし'
  //     const priorityText = todo.priority === 3 ? '高' : todo.priority === 2 ? '中' : '低'
  //     const dueDateText = todo.due_date
  //       ? `\n期限: ${format(new Date(todo.due_date), 'yyyy/MM/dd HH:mm', { locale: ja })}`
  //       : ''
  //     let descriptionText = ''
  //     if (todo.description) {
  //       const tempDiv = document.createElement('div')
  //       tempDiv.innerHTML = DOMPurify.sanitize(todo.description)
  //       descriptionText = tempDiv.textContent || tempDiv.innerText || ''
  //     }
  //     const shareText = `📋 ${todo.title}\n\n` +
  //       `プロジェクト: ${projectName}\n` +
  //       `優先度: ${priorityText}${dueDateText}\n` +
  //       (descriptionText ? `\n詳細:\n${descriptionText}` : '')
  //     const isNative = Capacitor.isNativePlatform()
  //     if (isNative) {
  //       await Share.share({ title: todo.title, text: shareText, dialogTitle: 'タスクを共有' })
  //     } else {
  //       if (navigator.share) {
  //         await navigator.share({ title: todo.title, text: shareText })
  //       } else {
  //         await navigator.clipboard.writeText(shareText)
  //         alert('タスク内容をクリップボードにコピーしました！')
  //       }
  //     }
  //   } catch (err: any) {
  //     if (err.message !== 'Share canceled' && err.name !== 'AbortError') {
  //       console.error('Share error:', err)
  //       alert('共有に失敗しました')
  //     }
  //   }
  // }

  const getStatusColor = (status: Todo['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'completed': return 'bg-gray-200 text-gray-600'
      case 'deleted': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const projectColor = todo.project?.color || '#6366F1'
  
  // 残り日数を計算（オン/オフ可能）
  const SHOW_DAYS_LEFT = true // これをfalseにすると残り日数表示が消える
  const getDaysLeft = () => {
    if (!todo.due_date || todo.status === 'completed') return null
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    // parseLocalDateを使用してタイムゾーンの問題を回避
    const dueDate = parseLocalDate(todo.due_date)
    const diffTime = dueDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }
  const daysLeft = getDaysLeft()
  
  // 完了タスクの場合はグレー系の配色にする
  const backgroundColor = todo.status === 'completed'
    ? '#f3f4f6' // 薄いグレー背景
    : '#ffffff' // 白背景

  // プロジェクトカラーを薄く重ねるための色（透明度15%）
  const overlayColor = todo.status === 'completed'
    ? 'transparent'
    : getColorWithOpacity(projectColor, 0.15)

  const borderColor = todo.status === 'completed'
    ? '#9ca3af' // グレーボーダー
    : projectColor // 完全なプロジェクトカラー

  if (isEditing) {
    return (
      <div
        className="p-4 rounded-r-lg border border-gray-300"
        style={{
          background: `linear-gradient(${overlayColor}, ${overlayColor}), ${backgroundColor}`
        }}
      >
        <div className="space-y-3">
          <div>
            <input
              type="text"
              value={editForm.title}
              onChange={handleTitleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                titleError ? 'border-red-500 focus:ring-red-500' : 'focus:ring-indigo-500'
              }`}
              placeholder="タスク名"
            />
            {titleError && (
              <p className="text-red-600 text-xs sm:text-sm mt-1">{titleError}</p>
            )}
            {!titleError && editForm.title.length > MAX_TITLE_LENGTH * 0.8 && (
              <p className="text-yellow-600 text-xs sm:text-sm mt-1">
                残り {MAX_TITLE_LENGTH - editForm.title.length} 文字
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">詳細（任意）</label>
            <RichTextEditor
              content={editForm.description}
              onChange={(html) => setEditForm(prev => ({ ...prev, description: html }))}
              placeholder="タスクの詳細を入力..."
            />
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">プロジェクト</label>
              <select
                value={editForm.projectId}
                onChange={(e) => setEditForm(prev => ({ ...prev, projectId: e.target.value }))}
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            {/* 期間設定 */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">期間</label>
              <div className="flex items-center gap-2">
                {/* 開始日 */}
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">開始日</label>
                  <div
                    className="relative cursor-pointer"
                    onClick={() => startDateInputRef.current?.showPicker?.()}
                  >
                    <input
                      ref={startDateInputRef}
                      type="date"
                      value={editForm.startDate}
                      onChange={(e) => setEditForm(prev => ({ ...prev, startDate: e.target.value }))}
                      max={editForm.date || undefined}
                      className="w-full h-9 px-2 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                      style={{ colorScheme: 'light' }}
                    />
                    <div className="h-9 px-2 text-xs sm:text-sm border rounded-lg bg-gray-50 flex items-center pointer-events-none">
                      {editForm.startDate || 'YYYY-MM-DD'}
                    </div>
                    {editForm.startDate && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          clearStartDate()
                        }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                        title="開始日をクリア"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>

                <span className="text-gray-400 mt-5">〜</span>

                {/* 期限 */}
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">期限</label>
                  <div
                    className="relative cursor-pointer"
                    onClick={() => dateInputRef.current?.showPicker?.()}
                  >
                    <input
                      ref={dateInputRef}
                      type="date"
                      value={editForm.date}
                      onChange={handleDateChange}
                      min={editForm.startDate || undefined}
                      className="w-full h-9 px-2 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                      style={{ colorScheme: 'light' }}
                    />
                    <div className="h-9 px-2 text-xs sm:text-sm border rounded-lg bg-gray-50 flex items-center pointer-events-none">
                      {editForm.date || 'YYYY-MM-DD'}
                    </div>
                    {editForm.date && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          clearDate()
                        }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                        title="期限をクリア"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* 時間選択（日付選択後に表示） */}
              {editForm.date && (
                <div className="mt-3">
                  <label className="block text-xs text-gray-500 mb-1">
                    期限時刻（任意）
                  </label>
                  <div
                    className="relative cursor-pointer"
                    style={{ width: '120px' }}
                    onClick={() => timeInputRef.current?.showPicker?.()}
                  >
                    <input
                      type="time"
                      ref={timeInputRef}
                      value={editForm.time}
                      onChange={handleTimeChange}
                      className="w-full h-9 px-3 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                      style={{ colorScheme: 'light' }}
                    />
                    <div className="h-9 px-3 text-sm border rounded-lg bg-gray-50 flex items-center pointer-events-none">
                      {editForm.time || '--:--'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* 区切り線を追加してセパレート */}
          <div className="border-t pt-4 mt-2">
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsEditing(false)
                  setTitleError('')
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                <X className="h-5 w-5" />
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Check className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const showThumbnail = thumbnailUrl

  return (
    <>
      {/* スワイプ確定時のオーバーレイ（タップでリセット） */}
      {isSwipeConfirmed && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => {
            setSwipeOffset(0)
            setSwipedTodoId(null)
            setIsSwipeConfirmed(false)
          }}
        />
      )}

      <div id={`task-${todo.id}`} className="relative z-40 task-item-enter">
        {/* 背景ボタン（左スワイプ時：削除）※確定後のみ表示 */}
        {isSwipeConfirmed && swipeOffset < 0 && (
          <div className="absolute inset-0 flex items-center justify-end pr-2">
            <button
              onClick={() => {
                setSwipeOffset(0)
                setSwipedTodoId(null)
                setIsSwipeConfirmed(false)
                setShowDeleteConfirm(true)
              }}
              className="h-full px-4 bg-red-600 text-white rounded flex items-center justify-center font-bold"
            >
              削除
            </button>
          </div>
        )}

        {/* 背景ボタン（右スワイプ時：完了/未完了）※確定後のみ表示 */}
        {isSwipeConfirmed && swipeOffset > 0 && (
          <div className="absolute inset-0 flex items-center justify-start pl-2">
            <button
              onClick={() => {
                setSwipeOffset(0)
                setSwipedTodoId(null)
                setIsSwipeConfirmed(false)
                const newStatus = todo.status === 'completed' ? 'pending' : 'completed'
                handleStatusChange(newStatus)
              }}
              className="h-full px-4 bg-green-600 text-white rounded flex items-center justify-center font-bold"
            >
              {todo.status === 'completed' ? '未完了' : '完了'}
            </button>
          </div>
        )}

        <div
          {...swipeHandlers}
          onClick={() => {
            // 確定状態の場合はリセット（少し戻す操作の代わり）
            if (isSwipeConfirmed) {
              setSwipeOffset(0)
              setSwipedTodoId(null)
              setIsSwipeConfirmed(false)
            }
          }}
          className={`rounded-r-lg px-2 sm:px-3 py-1 sm:py-2 hover:opacity-90 transition-all border-l-4 sm:border-l-8 border border-gray-300 relative ${
            todo.status === 'completed' ? 'opacity-60' : ''
          } ${isDeleting ? 'opacity-0' : ''}`}
          style={{
            background: `linear-gradient(${overlayColor}, ${overlayColor}), ${backgroundColor}`,
            borderLeftColor: borderColor,
            transform: `translateX(${swipeOffset}px)`,
            transition: swipeOffset === 0 ? 'transform 0.3s ease-out, opacity 0.3s' : 'none'
          }}
        >
          <div className="flex flex-col gap-2 sm:gap-3 w-full">
        {/* ステータスとプロジェクトバッジ（最上部） */}
        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
          <select
            ref={statusSelectRef}
            value={todo.status}
            onChange={(e) => handleStatusChange(e.target.value as Todo['status'])}
            disabled={isViewer}
            className={`px-1 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded-full font-medium ${getStatusColor(todo.status)} ${isViewer ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            data-tour="task-status"
          >
            <option value="pending">未着手</option>
            <option value="in_progress">進行中</option>
            <option value="completed">完了</option>
            {todo.status === 'deleted' && <option value="deleted">ゴミ箱</option>}
          </select>

          {todo.project && (
            <select
              value={todo.project_id}
              onChange={(e) => handleProjectChange(e.target.value)}
              disabled={isViewer}
              className={`flex items-center gap-1 px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-bold rounded-full bg-white border border-gray-200 ${isViewer ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'}`}
              style={{
                color: todo.status === 'completed'
                  ? '#9ca3af'
                  : todo.project.color
              }}
              data-tour="task-project"
            >
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          )}

          <div className="hidden sm:flex items-center gap-0.5 text-xs text-gray-500">
            <Clock className="h-3 w-3" />
            <span>登録: {format(new Date(todo.created_at), 'M/d', { locale: ja })}</span>
          </div>

          {/* 担当者バッジ */}
          {todo.assigned_user && (
            <AssignedUserBadge assignedUser={todo.assigned_user} size="medium" />
          )}
        </div>

        {/* メインコンテンツ */}
        <div className="w-full relative">
          {showThumbnail ? (
            // 画像がある場合：タスク名以下すべてを2カラムレイアウトで表示
            <div className="flex gap-2 items-start">
              {/* 左カラム: タスク名・期限・説明文 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <h3 className={`task-title font-medium leading-tight ${
                    todo.status === 'completed'
                      ? 'line-through text-gray-400'
                      : 'text-gray-900'
                  } break-words`}>
                    {todo.title}
                  </h3>
                  {/* 期限表示（タスク名の横に表示） */}
                  {SHOW_DAYS_LEFT && daysLeft !== null && todo.due_date && (() => {
                    // parseLocalDateTimeを使用してタイムゾーンの問題を回避
                    const dueDateTime = parseLocalDateTime(todo.due_date)
                    const hasTime = dueDateTime.getHours() !== 0 || dueDateTime.getMinutes() !== 0
                    const dateFormat = hasTime ? 'M/d HH:mm' : 'M/d'
                    return (
                      <span className={`text-meta font-medium px-1.5 py-0.5 sm:px-2 rounded-full whitespace-nowrap flex items-center gap-0.5 sm:gap-1 flex-shrink-0 ${
                        daysLeft < 0
                          ? 'bg-red-500 text-white'
                          : daysLeft === 0
                            ? 'bg-orange-500 text-white'
                            : daysLeft <= 3
                              ? 'bg-yellow-400 text-yellow-900'
                              : daysLeft <= 7
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700'
                      }`}>
                        {daysLeft < 0
                          ? `${t('todo.daysOverdue', { count: Math.abs(daysLeft) })} ${format(dueDateTime, dateFormat)}`
                          : daysLeft === 0
                            ? `${t('todo.dueToday')} ${format(dueDateTime, dateFormat)}`
                            : `${t('todo.daysLeft', { count: daysLeft })} ${format(dueDateTime, dateFormat)}`}
                      </span>
                    )
                  })()}
                </div>

                {/* 進捗バー */}
                {todo.progress_percentage > 0 && (
                  <div className="mt-2">
                    <ProgressBar percentage={todo.progress_percentage} size="small" />
                  </div>
                )}

                {/* 説明文 */}
                {todo.description && (
                  <div
                    className={`mt-1 task-description leading-tight ${
                      todo.status === 'completed'
                        ? 'text-gray-400'
                        : 'text-gray-600'
                    } break-words pointer-events-none`}
                  >
                    {todo.description.includes('<') && todo.description.includes('>') ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(todo.description, {
                            ADD_ATTR: ['target', 'rel']
                          })
                        }}
                      />
                    ) : (
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline pointer-events-auto" />
                          ),
                          p: ({ node, ...props }) => (
                            <p {...props} className="my-0.5 break-words w-full" />
                          ),
                          strong: ({ node, ...props }) => (
                            <strong {...props} className="font-bold break-words inline" />
                          ),
                          br: () => <br />
                        }}
                      >
                        {todo.description.replace(/\n/g, '  \n')}
                      </ReactMarkdown>
                    )}
                  </div>
                )}
              </div>

              {/* 右カラム: サムネイル画像（クリックで全画面ビューアを開く） */}
              <div className="flex-shrink-0 self-start">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setViewerImageId(todo.thumbnail_image_id ?? null)
                    setShowImageModal(true)
                  }}
                  className="block rounded-lg tap-highlight"
                  aria-label="画像を全画面表示"
                >
                  <img
                    src={thumbnailUrl}
                    alt="サムネイル"
                    className={`object-cover rounded-lg shadow-sm cursor-pointer hover:opacity-85 transition-opacity ${
                      todo.thumbnail_size === 'large'
                        ? 'w-32 h-32 sm:w-40 sm:h-40'
                        : 'w-16 h-16 sm:w-20 sm:h-20'
                    }`}
                    style={{ imageRendering: 'crisp-edges' as any }}
                  />
                </button>
              </div>
            </div>
          ) : (
            // 画像がない場合：横幅いっぱいに表示
            <>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <h3 className={`task-title font-medium leading-tight ${
                  todo.status === 'completed'
                    ? 'line-through text-gray-400'
                    : 'text-gray-900'
                } break-words`}>
                  {todo.title}
                </h3>
                {/* 期限表示（タスク名の横に表示） */}
                {SHOW_DAYS_LEFT && daysLeft !== null && todo.due_date && (() => {
                  // parseLocalDateTimeを使用してタイムゾーンの問題を回避
                  const dueDateTime = parseLocalDateTime(todo.due_date)
                  const hasTime = dueDateTime.getHours() !== 0 || dueDateTime.getMinutes() !== 0
                  const dateFormat = hasTime ? 'M/d HH:mm' : 'M/d'
                  return (
                    <span className={`text-meta font-medium px-1.5 py-0.5 sm:px-2 rounded-full whitespace-nowrap flex items-center gap-0.5 sm:gap-1 flex-shrink-0 ${
                      daysLeft < 0
                        ? 'bg-red-500 text-white'
                        : daysLeft === 0
                          ? 'bg-orange-500 text-white'
                          : daysLeft <= 3
                            ? 'bg-yellow-400 text-yellow-900'
                            : daysLeft <= 7
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700'
                    }`}>
                      {daysLeft < 0
                        ? `${t('todo.daysOverdue', { count: Math.abs(daysLeft) })} ${format(dueDateTime, dateFormat)}`
                        : daysLeft === 0
                          ? `${t('todo.dueToday')} ${format(dueDateTime, dateFormat)}`
                          : `${t('todo.daysLeft', { count: daysLeft })} ${format(dueDateTime, dateFormat)}`}
                    </span>
                  )
                })()}
              </div>

              {/* 進捗バー */}
              {todo.progress_percentage > 0 && (
                <div className="mt-2">
                  <ProgressBar percentage={todo.progress_percentage} size="small" />
                </div>
              )}

              {/* 説明文 */}
              {todo.description && (
                <div
                  className={`mt-1 task-description leading-tight ${
                    todo.status === 'completed'
                      ? 'text-gray-400'
                      : 'text-gray-600'
                  } break-words w-full pointer-events-none`}
                >
                  {todo.description.includes('<') && todo.description.includes('>') ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(todo.description, {
                          ADD_ATTR: ['target', 'rel']
                        })
                      }}
                    />
                  ) : (
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline pointer-events-auto" />
                        ),
                        p: ({ node, ...props }) => (
                          <p {...props} className="my-0.5 break-words w-full" />
                        ),
                        strong: ({ node, ...props }) => (
                          <strong {...props} className="font-bold break-words inline" />
                        ),
                        br: () => <br />
                      }}
                    >
                      {todo.description.replace(/\n/g, '  \n')}
                    </ReactMarkdown>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* アイコン行（最下部・常に右揃え） - ゴミ箱では非表示、閲覧者では非表示 */}
        {/* PC版: 120%サイズ(h-7 w-7)、10px間隔(gap-[10px]) / モバイル: 120%サイズ(h-6 w-6)、2px間隔 */}
        {todo.status !== 'deleted' && !isViewer && (
          <div className="flex items-center justify-end gap-2 sm:gap-[10px] w-full" data-tour="task-buttons">
              <Tooltip content="画像を管理" position="top">
                <button
                  onClick={() => {
                    setViewerImageId(null) // アイコンからは従来通りリスト表示で開く
                    setShowImageModal(true)
                  }}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded relative tap-highlight"
                  aria-label="画像"
                  data-tour="task-images"
                >
                  <img src={ButtonH} alt="画像を管理" className="h-6 w-6 sm:h-7 sm:w-7" />
                  {imageCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-indigo-600 text-white text-[10px] sm:text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center font-medium">
                      {imageCount}
                    </span>
                  )}
                </button>
              </Tooltip>
              <Tooltip content="ファイルを管理" position="top">
                <button
                  onClick={() => setShowFileModal(true)}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded relative tap-highlight"
                  aria-label="ファイル"
                  data-tour="task-files"
                >
                  <img src={ButtonI} alt="ファイルを管理" className="h-6 w-6 sm:h-7 sm:w-7" />
                  {fileCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-green-600 text-white text-[10px] sm:text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center font-medium">
                      {fileCount}
                    </span>
                  )}
                </button>
              </Tooltip>

              {/* チームプロジェクトの場合のみ表示 */}
              {todo.project?.is_team_project && (
                <>
                  <Tooltip content="担当者を設定" position="top">
                    <button
                      onClick={() => setShowAssignModal(true)}
                      className="p-1.5 sm:p-2 hover:bg-gray-100 rounded tap-highlight"
                      aria-label="担当者を設定"
                    >
                      <img src={ButtonJ} alt="担当者を設定" className="h-6 w-6 sm:h-7 sm:w-7" />
                    </button>
                  </Tooltip>
                  <Tooltip content="進捗メッセージ" position="top">
                    <button
                      onClick={() => setShowProgressPanel(true)}
                      className="p-1.5 sm:p-2 hover:bg-gray-100 rounded tap-highlight"
                      aria-label="進捗メッセージ"
                    >
                      <img src={ButtonK} alt="進捗メッセージ" className="h-6 w-6 sm:h-7 sm:w-7" />
                    </button>
                  </Tooltip>
                </>
              )}

              <Tooltip content="タスクを編集" position="top">
                <button
                  onClick={() => {
                    // Reset form with current todo values
                    // プレーンテキストの改行をHTMLに変換（TipTapエディタ用）
                    let descriptionHtml = todo.description || ''
                    // HTMLタグが含まれていない場合（プレーンテキスト）は改行を<br>に変換
                    if (descriptionHtml && !descriptionHtml.includes('<')) {
                      descriptionHtml = descriptionHtml
                        .split('\n')
                        .map(line => `<p>${line || '<br>'}</p>`)
                        .join('')
                    }
                    setEditForm({
                      title: todo.title,
                      description: descriptionHtml,
                      startDate: toDateOnly(todo.start_date),
                      dueDate: toDateTimeLocal(todo.due_date),
                      date: toDateOnly(todo.due_date),
                      time: toTimeOnly(todo.due_date),
                      projectId: todo.project_id
                    })
                    setIsEditing(true)
                  }}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded tap-highlight"
                  aria-label="編集"
                  data-tour="task-edit"
                >
                  <img src={ButtonE} alt="タスクを編集" className="h-6 w-6 sm:h-7 sm:w-7" />
                </button>
              </Tooltip>
              {/* タスク複製ボタン */}
              <Tooltip content="タスクを複製" position="top">
                <button
                  onClick={handleDuplicateTask}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded tap-highlight"
                  aria-label="タスクを複製"
                >
                  <img src={ButtonL} alt="タスクを複製" className="h-6 w-6 sm:h-7 sm:w-7" />
                </button>
              </Tooltip>
              {/* SNS共有ボタン - 機能が未完成のため一時的に非表示 */}
              {false && todo.status === 'completed' && (
                <Tooltip content="SNSでシェア" position="top">
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="p-2 sm:p-2.5 text-gray-600 hover:bg-gray-100 rounded"
                    aria-label="SNSでシェア"
                  >
                    <Share2 className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </Tooltip>
              )}
              {/* 通知設定ボタン（ネイティブアプリのみ、期限がある場合のみ） */}
              {Capacitor.isNativePlatform() && todo.due_date && (
                <Tooltip content={hasNotification ? "通知をキャンセル" : "通知を設定"} position="top">
                  <button
                    onClick={hasNotification ? cancelNotification : scheduleNotification}
                    className={`p-1 sm:p-1.5 rounded ${
                      hasNotification
                        ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    aria-label={hasNotification ? "通知をキャンセル" : "通知を設定"}
                  >
                    <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </Tooltip>
              )}
              {/* カレンダーエクスポートボタン（期限がある場合のみ） - 一時的に非表示 */}
              {false && todo.due_date && (
                <Tooltip content="カレンダーに追加" position="top">
                  <button
                    onClick={handleExportToCalendar}
                    className="p-1 sm:p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                    aria-label="カレンダーに追加"
                  >
                    <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </Tooltip>
              )}
              <Tooltip content="ゴミ箱に移動" position="top">
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-1.5 sm:p-2 hover:bg-red-50 rounded tap-highlight"
                  aria-label="ゴミ箱に移動"
                  data-tour="task-delete"
                >
                  <img src={ButtonF} alt="ゴミ箱に移動" className="h-6 w-6 sm:h-7 sm:w-7" />
                </button>
              </Tooltip>
            </div>
          )}
      </div>
      </div>
      </div>

      {/* Image Modal */}
      <TodoImageModal
        todoId={todo.id}
        todoTitle={todo.title}
        isOpen={showImageModal}
        initialImageId={viewerImageId}
        currentThumbnailId={todo.thumbnail_image_id}
        currentThumbnailSize={todo.thumbnail_size}
        onSetThumbnail={async (imageId, size) => {
          // Update database only - thumbnail URL will be updated by useEffect
          await updateTodo(todo.id, { thumbnail_image_id: imageId, thumbnail_size: size })
        }}
        onClose={() => {
          setShowImageModal(false)
          // Reload counts after closing modal (thumbnail URL will be updated by useEffect)
          getTodoImages(todo.id).then(result => {
            if (result.success && result.images) {
              setImageCount(result.images.length)
            }
          })
          getTodoFiles(todo.id).then(result => {
            if (result.success && result.files) {
              setFileCount(result.files.length)
            }
          })
        }}
      />

      {/* File Modal */}
      <TodoFileModal
        todoId={todo.id}
        todoTitle={todo.title}
        isOpen={showFileModal}
        onClose={() => {
          setShowFileModal(false)
          // Reload file count after closing modal
          getTodoFiles(todo.id).then(result => {
            if (result.success && result.files) {
              setFileCount(result.files.length)
            }
          })
        }}
      />

      {/* Delete Confirmation Dialog - iOS風のダイアログ */}
      {showDeleteConfirm && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="bg-gray-800 rounded-2xl max-w-xs w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* タイトルと説明 */}
            <div className="p-6 text-center">
              <h3 className="text-base font-semibold text-white mb-3">
                タスクの削除確認
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed">
                このタスクをゴミ箱に移動しますか？
              </p>
              <p className="text-xs text-gray-400 mt-3 leading-relaxed">
                ※ ゴミ箱から完全に削除するか、復元することができます。
              </p>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                ※ 添付された画像・ファイルは削除されません。
              </p>
            </div>

            {/* ボタン */}
            <div className="border-t border-gray-700">
              <button
                onClick={async () => {
                  setShowDeleteConfirm(false)

                  // ユーザー操作内でAudioContextを初期化
                  initializeFeedback()

                  setIsDeleting(true)
                  setTimeout(async () => {
                    await updateTodo(todo.id, {
                      status: 'deleted',
                      deleted_at: new Date().toISOString()
                    })
                    // 削除時の効果音フィードバック
                    triggerFeedback('delete', { soundEnabled })
                  }, 300)
                }}
                className="w-full py-3 text-center text-base font-semibold text-blue-400 hover:bg-gray-700 transition-colors border-b border-gray-700"
              >
                OK
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full py-3 text-center text-base font-medium text-blue-400 hover:bg-gray-700 transition-colors"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* チーム機能モーダル */}
      {todo.project?.is_team_project && todo.project.team_id && (
        <>
          {showAssignModal && createPortal(
            <div onClick={() => setShowAssignModal(false)}>
              <div onClick={(e) => e.stopPropagation()}>
                <AssignTaskModal
                  isOpen={showAssignModal}
                  onClose={() => setShowAssignModal(false)}
                  todoTitle={todo.title}
                  teamId={todo.project.team_id}
                  currentAssignedUserId={todo.assigned_to_user_id}
                  onSelect={async (userId) => {
                    // 選択後にDBに保存
                    await updateTodo(todo.id, {
                      assigned_to_user_id: userId,
                      assignment_status: userId ? 'assigned' : 'unassigned'
                    })
                    // タスク一覧を再読み込み
                    const { fetchTodos, selectedProjectId } = useStore.getState()
                    await fetchTodos(selectedProjectId ?? undefined)
                  }}
                />
              </div>
            </div>,
            document.body
          )}

          {showProgressPanel && createPortal(
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowProgressPanel(false)}
            >
              <div
                className="max-w-sm w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <TaskProgressPanel
                  currentProgress={todo.progress_percentage}
                  onProgressSelect={async (progress: number) => {
                    // 進捗率を更新
                    await updateTodo(todo.id, { progress_percentage: progress })
                    // タスク一覧を再読み込み
                    const { fetchTodos, selectedProjectId } = useStore.getState()
                    await fetchTodos(selectedProjectId ?? undefined)
                    setShowProgressPanel(false)
                  }}
                  onClose={() => setShowProgressPanel(false)}
                />
              </div>
            </div>,
            document.body
          )}
        </>
      )}

      {/* SNS共有モーダル */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        todo={todo}
        shareType="task_completion"
      />

      {/* プロジェクト変更確認ダイアログ */}
      <ConfirmDialog
        isOpen={showProjectChangeConfirm}
        title="プロジェクトを移動"
        message="このタスクを別のプロジェクトに移動しますか？"
        confirmText="移動する"
        cancelText="キャンセル"
        variant="info"
        onConfirm={handleProjectChangeConfirm}
        onCancel={() => {
          setShowProjectChangeConfirm(false)
          setPendingProjectId(null)
        }}
      />

    </>
  )
}